'use client';

/**
 * Drukbestand bij een printbaar accessoire (bijvoorbeeld een bedrukte
 * draagtas naast flyers).
 *
 * Waarom dit bestaat: de configurator bewaarde alleen de naam en grootte van
 * het gekozen bestand in de winkelwagen, met de belofte dat de kassa het later
 * zou uploaden. Dat deed niets: een browser-`File` overleeft de navigatie naar
 * de winkelwagen niet en staat niet in de Zustand-opslag. De bestelregel van
 * het accessoire kreeg dus nooit een printbestand. Nu uploaden we direct bij
 * het kiezen, met precies dezelfde beslissingen als de gewone uploader
 * (`file-uploader.tsx`): EPS/PS/oude AI eerst bewaren en om een PDF vragen,
 * SVG markeren voor omzetting, de rest rechtstreeks.
 */

import type { CartItem } from '@/store/cart-store';
import { prepareArtworkFile, uploadFileToStorage, type RegisteredUpload } from './client-upload';
import { POSTSCRIPT_MIME } from './artwork-formats';
import { uploadAndConvertPostScript } from '@/lib/artwork/client-artwork';

export type AccessoryUploadedFile = NonNullable<CartItem['uploadedFile']>;

export type AccessoryUploadState =
  | { status: 'uploading'; fileName: string; requestId: number }
  | { status: 'done'; fileName: string; requestId: number; file: AccessoryUploadedFile }
  | { status: 'error'; fileName: string; requestId: number; error: string };

export async function uploadAccessoryArtwork(file: File, options: {
  productId?: string;
  copy: { failed: string; invalid: string; timedOut?: string };
}): Promise<AccessoryUploadedFile> {
  const { file: preparedFile, detection } = await prepareArtworkFile(file, options.copy.invalid);
  let upload: RegisteredUpload;
  let conversionRequired = false;
  let sourceFile: { id: string; url: string; fileName: string; fileSize: number } | undefined;

  if (detection?.mimeType === POSTSCRIPT_MIME) {
    const result = await uploadAndConvertPostScript(preparedFile, {
      copy: { failed: options.copy.failed, invalid: options.copy.invalid },
      productId: options.productId,
      providerType: 'PRINTCOM',
    });
    if (result.converted) {
      upload = result.converted;
      sourceFile = { id: result.original.id, url: result.original.url, fileName: result.original.fileName, fileSize: result.original.fileSize };
    } else {
      upload = result.original;
      conversionRequired = true;
    }
  } else {
    upload = await uploadFileToStorage(preparedFile, {
      productId: options.productId,
      providerType: 'PRINTCOM',
      failureMessage: options.copy.failed,
      invalidFileMessage: options.copy.invalid,
      timeoutMessage: options.copy.timedOut,
      step: 'file-upload',
    });
    // Een SVG kan de browser tonen, maar de drukker wil PDF.
    if (detection?.needsConversion) conversionRequired = true;
  }

  return {
    id: upload.id,
    fileName: upload.fileName,
    fileSize: upload.fileSize,
    url: upload.url,
    ...(upload.providerFileId ? { providerFileId: upload.providerFileId } : {}),
    ...(sourceFile || conversionRequired ? { designData: {
      ...(sourceFile ? { sourceFile } : {}),
      ...(conversionRequired ? { artworkConversion: { required: true, files: [{ uploadId: upload.id, url: upload.url, fileName: upload.fileName }] } } : {}),
    } } : {}),
  };
}

/**
 * Mag de klant nu naar de winkelwagen? Niet zolang een gekozen bestand nog
 * onderweg is of is mislukt: anders belandt het accessoire zonder drukbestand
 * in de bestelling, precies de fout die dit moest oplossen. Geen bestand
 * kiezen blijft toegestaan (dat was het al).
 */
export function accessoryUploadBlocker(
  selectedAccessoryIds: Iterable<string>,
  isPrintable: (id: string) => boolean,
  uploads: Record<string, AccessoryUploadState | undefined>,
): { id: string; reason: 'uploading' | 'error' } | null {
  for (const id of selectedAccessoryIds) {
    if (!isPrintable(id)) continue;
    const state = uploads[id];
    if (state?.status === 'uploading') return { id, reason: 'uploading' };
    if (state?.status === 'error') return { id, reason: 'error' };
  }
  return null;
}

/** Velden voor de winkelwagenregel van een accessoire. */
export function accessoryCartFileFields(state: AccessoryUploadState | undefined): {
  options: { accessoryFileName?: string; accessoryFileSize?: number };
  uploadedFile?: AccessoryUploadedFile;
} {
  if (state?.status !== 'done') return { options: {} };
  return {
    options: { accessoryFileName: state.file.fileName, accessoryFileSize: state.file.fileSize },
    uploadedFile: state.file,
  };
}

export const ACCESSORY_UPLOAD_COPY = {
  nl: { needed: 'Bestand nodig voor dit accessoire', choose: 'Kies bestand', uploading: 'Uploaden...', remove: 'Bestand verwijderen', failed: 'Uploaden mislukt. Kies het bestand opnieuw.', timedOut: 'Het uploaden duurde te lang. Probeer het opnieuw.', waitForUpload: 'Even wachten: het bestand voor je accessoire wordt nog geüpload.', fixUpload: 'Het bestand voor je accessoire is niet geüpload. Kies het opnieuw of verwijder het.' },
  en: { needed: 'File needed for this accessory', choose: 'Choose file', uploading: 'Uploading...', remove: 'Remove file', failed: 'Upload failed. Please choose the file again.', timedOut: 'The upload took too long. Please try again.', waitForUpload: 'One moment: the file for your accessory is still uploading.', fixUpload: 'The file for your accessory was not uploaded. Choose it again or remove it.' },
  de: { needed: 'Datei für dieses Zubehör erforderlich', choose: 'Datei wählen', uploading: 'Wird hochgeladen...', remove: 'Datei entfernen', failed: 'Hochladen fehlgeschlagen. Bitte wählen Sie die Datei erneut.', timedOut: 'Das Hochladen hat zu lange gedauert. Bitte versuchen Sie es erneut.', waitForUpload: 'Einen Moment: Die Datei für Ihr Zubehör wird noch hochgeladen.', fixUpload: 'Die Datei für Ihr Zubehör wurde nicht hochgeladen. Wählen Sie sie erneut oder entfernen Sie sie.' },
  fr: { needed: 'Fichier requis pour cet accessoire', choose: 'Choisir un fichier', uploading: 'Envoi en cours...', remove: 'Supprimer le fichier', failed: 'L’envoi a échoué. Veuillez choisir à nouveau le fichier.', timedOut: 'L’envoi a pris trop de temps. Veuillez réessayer.', waitForUpload: 'Un instant : le fichier de votre accessoire est encore en cours d’envoi.', fixUpload: 'Le fichier de votre accessoire n’a pas été envoyé. Choisissez-le à nouveau ou supprimez-le.' },
} as const;

export function accessoryUploadCopy(locale: string | null | undefined) {
  const key = String(locale || 'nl').toLowerCase().split('-')[0] as keyof typeof ACCESSORY_UPLOAD_COPY;
  return ACCESSORY_UPLOAD_COPY[key] || ACCESSORY_UPLOAD_COPY.en;
}
